import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import styles from "./PinnedLottieSection.module.css";
import { useIsLg } from "./useIsLg";

const LazyPinnedLottie = lazy(() => import("./PinnedLottie"));

// Scroll checkpoints for the pinned track, matched to the reference build.
const PIN = {
  titleFadeStart: 0.03,
  lottieEaseStart: 0.02,
  lottieStart: 0.22,
  desktopTitleExitY: -560,
  mobileTitleExitY: -280,
  desktopLottieEnterPx: 140,
  mobileLottieEnterPx: 28,
};

const clamp01 = (n) => Math.min(1, Math.max(0, n));

/**
 * A full-height pinned track: the heading holds, fades out as you scroll, and
 * the Lottie beneath it scrubs frame-by-frame against scroll position.
 */
export default function PinnedLottieSection({
  eyebrow,
  heading,
  desktopSrc,
  mobileSrc,
  ariaLabel,
  leadIn,
}) {
  const trackRef = useRef(null);
  const sentinelRef = useRef(null);
  const isLg = useIsLg();
  const [animationData, setAnimationData] = useState(null);

  // The Lottie JSONs are 1-2MB each, so only fetch once the section is close to
  // the viewport, and fetch the variant matching the current breakpoint.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    let cancelled = false;
    const src = isLg ? desktopSrc : mobileSrc;

    const load = () => {
      Promise.all([
        fetch(src).then((r) => r.json()),
        import("./PinnedLottie"),
      ])
        .then(([data]) => {
          if (!cancelled) setAnimationData(data);
        })
        .catch(() => {
          /* leave the section as a blank card if the asset fails */
        });
    };

    if (typeof IntersectionObserver === "undefined") {
      load();
      return () => {
        cancelled = true;
      };
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        load();
      },
      { rootMargin: "500px" }
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [isLg, desktopSrc, mobileSrc]);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  const titleOpacity = useTransform(scrollYProgress, (p) => {
    const v = clamp01(p);
    if (v <= PIN.titleFadeStart) return 1;
    if (v >= PIN.lottieStart) return 0;
    return 1 - (v - PIN.titleFadeStart) / (PIN.lottieStart - PIN.titleFadeStart);
  });

  const titleY = useTransform(scrollYProgress, (p) => {
    const v = clamp01(p);
    const exit = isLg ? PIN.desktopTitleExitY : PIN.mobileTitleExitY;
    if (v <= PIN.titleFadeStart) return 0;
    if (v >= PIN.lottieStart) return exit;
    return (
      exit *
      ((v - PIN.titleFadeStart) / (PIN.lottieStart - PIN.titleFadeStart))
    );
  });

  const lottieEnterY = useTransform(scrollYProgress, (p) => {
    const v = clamp01(p);
    const from = isLg ? PIN.desktopLottieEnterPx : PIN.mobileLottieEnterPx;
    if (v <= PIN.lottieEaseStart) return from;
    if (v >= PIN.lottieStart) return 0;
    const t = (v - PIN.lottieEaseStart) / (PIN.lottieStart - PIN.lottieEaseStart);
    return from * (1 - (1 - (1 - t) ** 3));
  });

  const lottieScrub = useTransform(scrollYProgress, (p) => {
    const v = clamp01(p);
    if (v < PIN.lottieStart) return 0;
    const span = 1 - PIN.lottieStart;
    return span > 0 ? (v - PIN.lottieStart) / span : 1;
  });

  return (
    <section
      className={styles.section}
      style={leadIn ? { "--mc-pin-lead-in": leadIn } : undefined}
    >
      <div className={styles.container}>
        <div ref={trackRef} className={styles.track}>
          <div className={styles.sticky}>
            <motion.div
              className={styles.headingBlock}
              style={{ opacity: titleOpacity, y: titleY }}
            >
              <p className={styles.eyebrow}>{eyebrow}</p>
              <h2 className={styles.heading}>{heading}</h2>
            </motion.div>

            <motion.div className={styles.graphic} style={{ y: lottieEnterY }}>
              <span ref={sentinelRef} className={styles.sentinel} aria-hidden />
              {animationData && (
                <Suspense fallback={null}>
                  <LazyPinnedLottie
                    key={isLg ? "lg" : "sm"}
                    animationData={animationData}
                    scrubProgress={lottieScrub}
                    ariaLabel={ariaLabel}
                    className={styles.lottie}
                  />
                </Suspense>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
